import CookiePreferencesButton from '@/Components/GDPR/CookiePreferencesButton';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { Link } from '@inertiajs/react';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle,
    Facebook,
    Globe,
    Instagram,
    Linkedin,
    Loader2,
    Mail,
    MapPin,
    MessageSquare,
    Phone,
    Send,
    Twitter,
    Youtube,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSubscribe } from './useSubscribe';

interface FooterProps {
    navLinks: Array<{ href: string; label: string }>;
    companyInfo: {
        name: string;
        logo: string;
        description: string;
        email: string;
        phone: string;
        address: string;
    };
    socialLinks: Array<{ platform: string; url: string; color: string }>;
    stats: Array<{ label: string; value: string }>;
    services: Array<{ href: string; label: string; badge?: string }>;
}

const Footer: React.FC<FooterProps> = ({
    navLinks,
    companyInfo,
    socialLinks,
    stats,
    services = [],
}) => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [subscriptionStatus, setSubscriptionStatus] = useState<
        'idle' | 'error' | 'success'
    >('idle');
    const [showQuickContact, setShowQuickContact] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const { subscribe, isLoading } = useSubscribe();

    const handleNewsletterSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setSubscriptionStatus('error');
            return;
        }

        try {
            await subscribe(email);
            setSubscriptionStatus('success');
            setEmail('');
        } catch (error) {
            setSubscriptionStatus('error');
        }
    };

    const getSocialIcon = (platform: string) => {
        switch (platform.toLowerCase()) {
            case 'linkedin':
                return Linkedin;
            case 'twitter':
                return Twitter;
            case 'facebook':
                return Facebook;
            case 'instagram':
                return Instagram;
            case 'youtube':
                return Youtube;
            default:
                return Globe;
        }
    };

    const handleQuickMessage = () => {
        const phoneNumber =
            companyInfo?.phone?.replace(/\D/g, '') || '260971576708';
        const whatsappMessage = encodeURIComponent(message);
        window.open(
            `https://wa.me/${phoneNumber}?text=${whatsappMessage}`,
            '_blank',
        );
        setMessage('');
        setShowQuickContact(false);
    };

    return (
        <footer className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100 pb-8 pt-16 dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50">
            {/* Decorative Elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-1/4 -top-1/4 h-96 w-96 rounded-full bg-emerald-200/20 blur-3xl filter dark:bg-emerald-700/20" />
                <div className="absolute -bottom-1/4 -right-1/4 h-96 w-96 rounded-full bg-amber-200/20 blur-3xl filter dark:bg-amber-700/20" />
            </div>

            {/* Stats Section */}
            <div className="container mx-auto mb-8 px-4 sm:mb-12">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="hover:scale-102 group relative overflow-hidden rounded-lg bg-white/20 p-4 backdrop-blur-sm transition-all hover:shadow-md dark:bg-gray-800/20 sm:p-5"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 to-amber-600/10 opacity-0 transition-opacity group-hover:opacity-100" />
                            <span className="relative block bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-xl font-bold text-transparent sm:text-2xl md:text-3xl">
                                {stat.value}
                            </span>
                            <span className="relative text-sm text-gray-600 dark:text-gray-300 sm:text-base">
                                {stat.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="container mx-auto px-4">
                <div className="grid gap-8 md:grid-cols-4 lg:grid-cols-5">
                    {/* Company Info */}
                    <div className="px-6 md:col-span-2 lg:col-span-2">
                        <div className="relative mb-6 flex items-center gap-4">
                            <img
                                src={companyInfo.logo}
                                alt={`${companyInfo.name} Logo`}
                                className="h-16"
                                loading="lazy"
                            />
                            <h2 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent">
                                {companyInfo.name}
                            </h2>
                        </div>
                        <div className="mb-8">
                            <p className="text-gray-600 dark:text-gray-300">
                                {companyInfo.description.length > 300 ? (
                                    <>
                                        {showFullDescription ? (
                                            <>
                                                {companyInfo.description}
                                                <Button
                                                    variant="link"
                                                    className="h-auto p-0 text-emerald-600 dark:text-emerald-400"
                                                    onClick={() =>
                                                        setShowFullDescription(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    See Less
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {companyInfo.description.slice(
                                                    0,
                                                    200,
                                                )}
                                                ...
                                                <Button
                                                    variant="link"
                                                    className="h-auto p-0 text-emerald-600 dark:text-emerald-400"
                                                    onClick={() =>
                                                        setShowFullDescription(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Read More
                                                </Button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    companyInfo.description
                                )}
                            </p>
                        </div>

                        {/* Newsletter Signup */}
                        <div className="rounded-xl bg-white/20 p-6 backdrop-blur-sm dark:bg-gray-800/20">
                            <h5 className="mb-3 font-semibold">
                                Stay Connected with Agriculture Innovation
                            </h5>
                            <form
                                onSubmit={handleNewsletterSignup}
                                className="space-y-4"
                            >
                                <div className="relative">
                                    <Input
                                        type="email"
                                        placeholder="Enter your email for farming insights"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setSubscriptionStatus('idle');
                                        }}
                                        className="bg-white/50 pr-10 backdrop-blur-sm dark:bg-gray-900/50"
                                        disabled={
                                            isLoading ||
                                            subscriptionStatus === 'success'
                                        }
                                    />
                                    {subscriptionStatus === 'error' && (
                                        <AlertCircle
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500"
                                            size={20}
                                        />
                                    )}
                                    {subscriptionStatus === 'success' && (
                                        <CheckCircle
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"
                                            size={20}
                                        />
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-emerald-600 to-amber-600 transition-transform hover:scale-105 hover:from-emerald-700 hover:to-amber-700"
                                    disabled={
                                        isLoading ||
                                        subscriptionStatus === 'success'
                                    }
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Subscribing...
                                        </>
                                    ) : subscriptionStatus === 'success' ? (
                                        <>
                                            <CheckCircle className="mr-2 h-4 w-4" />
                                            Subscribed!
                                        </>
                                    ) : (
                                        <>
                                            <Send className="mr-2 h-4 w-4" />
                                            Subscribe for Updates
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                            Quick Links
                        </h4>
                        <nav className="space-y-3">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className="group flex items-center text-gray-600 transition-all hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                                >
                                    <ArrowRight className="mr-2 h-4 w-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                                    {link.label}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Services */}
                    <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                            Our Services
                        </h4>
                        <nav className="space-y-3">
                            {services.map((service) => (
                                <Link
                                    key={service.href}
                                    href={service.href}
                                    className="group flex items-center justify-between text-gray-600 transition-all hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                                >
                                    <span className="flex items-center">
                                        <ArrowRight className="mr-2 h-4 w-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                                        {service.label}
                                    </span>
                                    {service.badge && (
                                        <Badge
                                            variant="secondary"
                                            className="bg-emerald-100 text-xs dark:bg-emerald-900"
                                        >
                                            {service.badge}
                                        </Badge>
                                    )}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Contact Section */}
                    <div className="space-y-6 md:col-span-4 lg:col-span-1">
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                            Get in Touch
                        </h4>

                        {/* Quick Contact Form */}
                        {showQuickContact ? (
                            <div className="rounded-xl bg-white/20 p-4 backdrop-blur-sm dark:bg-gray-800/20 sm:p-6">
                                <Textarea
                                    placeholder="How can we help your agricultural journey?"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="mb-4 bg-white/50 dark:bg-gray-900/50"
                                    rows={3}
                                />
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <Button
                                        variant="outline"
                                        className="sm:flex-1"
                                        onClick={() =>
                                            setShowQuickContact(false)
                                        }
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className="bg-gradient-to-r from-emerald-600 to-amber-600 text-xs hover:from-emerald-700 hover:to-amber-700 sm:flex-1"
                                        onClick={handleQuickMessage}
                                        disabled={!message.trim()}
                                    >
                                        <MessageSquare className="mr-2 h-4 w-4" />
                                        WhatsApp
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                className="w-full transition-transform hover:scale-105"
                                onClick={() => setShowQuickContact(true)}
                            >
                                Start a Conversation
                            </Button>
                        )}

                        {/* Contact Details */}
                        <div className="space-y-4 rounded-xl bg-white/20 p-4 backdrop-blur-sm dark:bg-gray-800/20 sm:p-6">
                            <div className="group flex items-center space-x-3 text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400">
                                <Mail className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
                                <a
                                    href={`mailto:${companyInfo?.email}`}
                                    className="break-all text-sm sm:text-base"
                                >
                                    {companyInfo?.email || 'contact@gaphen.com'}
                                </a>
                            </div>
                            <div className="group flex items-center space-x-3 text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400">
                                <Phone className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
                                <a
                                    href={`tel:${companyInfo?.phone}`}
                                    className="text-sm sm:text-base"
                                >
                                    {companyInfo?.phone || '+260 - default'}
                                </a>
                            </div>
                            <div className="group flex items-start space-x-3 text-gray-600 dark:text-gray-300">
                                <MapPin className="mt-1 h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
                                <address className="text-sm not-italic sm:text-base">
                                    {companyInfo?.address ||
                                        'Plot 1234, Agricultural Innovation Park, Lusaka, Zambia'}
                                </address>
                            </div>

                            {/* Social Links - Moved inside contact details card */}
                            <div className="mt-6 flex flex-wrap justify-center gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                                {socialLinks.map(({ platform, url, color }) => {
                                    const Icon = getSocialIcon(platform);
                                    return (
                                        <a
                                            key={platform}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group relative rounded-full bg-white/20 p-2 backdrop-blur-sm transition-all hover:scale-110 hover:shadow-lg dark:bg-gray-800/20 sm:p-3"
                                            aria-label={platform}
                                        >
                                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-600/10 to-amber-600/10 opacity-0 transition-opacity group-hover:opacity-100" />
                                            <Icon
                                                className="h-4 w-4 transition-colors group-hover:text-emerald-600 dark:group-hover:text-emerald-400 sm:h-5 sm:w-5"
                                                style={{ color }}
                                            />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Copyright & Additional Links */}
                <div className="mt-16 space-y-6">
                    {/* Additional Links */}
                    <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
                        <Link
                            href="/legal/privacy"
                            className="text-sm text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            href="/legal/terms"
                            className="text-sm text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                        >
                            Terms of Service
                        </Link>
                        <Link
                            href="/sitemap"
                            className="text-sm text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                        >
                            Sitemap
                        </Link>
                        <Link
                            href="/legal/accessibility"
                            className="text-sm text-gray-600 transition-colors hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400"
                        >
                            Accessibility
                        </Link>
                        <CookiePreferencesButton
                            privacyPolicyUrl="/legal/privacy"
                            cookiePolicyUrl="/legal/privacy"
                            consentVersion="1.0"
                            className="text-sm"
                        />
                    </div>

                    {/* Divider */}
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                        </div>
                        <div className="relative flex justify-center">
                            <span className="bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100 px-4 text-sm text-gray-500 dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50 dark:text-gray-400">
                                Cultivating Innovation in Agriculture
                            </span>
                        </div>
                    </div>

                    {/* Copyright */}
                    <p className="text-center text-sm text-gray-600 dark:text-gray-300">
                        © {new Date().getFullYear()} {companyInfo.name}.
                        <span className="block sm:inline sm:before:content-['_·_']">
                            Empowering Farmers, Enriching Communities
                        </span>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
